import { Editor, Node } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      setCallout: () => ReturnType;
      toggleCallout: () => ReturnType;
      unsetCallout: () => ReturnType;
    };
  }
}

class CalloutMenuView {
  private editor: Editor;
  private menu: HTMLDivElement;
  private select: HTMLSelectElement;

  constructor({ editor, view }: { editor: Editor; view: EditorView }) {
    this.editor = editor;

    this.menu = document.createElement('div');
    this.menu.className = 'block-menu';
    this.menu.style.display = 'none';

    const label = document.createElement('label');
    label.textContent = '类型';
    label.className = 'block-menu-label';
    this.menu.appendChild(label);

    this.select = document.createElement('select');
    this.select.className = 'block-menu-select';
    const options: Record<string, string> = {
      note: '提示',
      check: '核对',
      caution: '注意',
      warning: '警告',
    };
    this.select.innerHTML = Object.keys(options)
      .map((value) => {
        return `<option value="${value}">${options[value]}</option>`;
      })
      .join('');
    this.select.addEventListener('change', (event) => {
      const value = (event.target as HTMLSelectElement).value;
      this.editor.commands.updateAttributes('callout', { type: value });
    });
    this.menu.appendChild(this.select);

    view.dom.parentNode.appendChild(this.menu);

    this.update(view, null);
  }

  update(view: EditorView, lastState: any) {
    let { state } = view;

    // Don't do anything if the document/selection didn't change
    if (lastState && lastState.doc.eq(state.doc) && lastState.selection.eq(state.selection)) {
      return;
    }

    // find callout node
    let callout = null;
    let calloutPos = null;
    for (let i = state.selection.$from.depth; i > 0; i--) {
      if (state.selection.$from.node(i).type.name === 'callout') {
        callout = state.selection.$from.node(i);
        calloutPos = state.selection.$from.before(i);
        break;
      }
    }

    if (callout === null) {
      this.menu.style.display = 'none';
      return;
    }

    this.select.value = callout.attrs.type;

    this.menu.style.display = 'flex';

    const coords = view.coordsAtPos(calloutPos);

    const box = this.menu.offsetParent.getBoundingClientRect();

    // update menu bottom
    this.menu.style.bottom = `${box.bottom - coords.top + 8}px`;
  }
}

const Callout = Node.create({
  name: 'callout',

  group: 'block',

  content: 'block+',

  addAttributes() {
    return {
      type: {
        default: 'note',
        parseHTML: (element) => {
          return element.getAttribute('data-callout-type');
        },
        renderHTML: (attributes) => {
          console.log(attributes);
          return {
            'data-callout-type': attributes.type,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-callout-type]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setCallout:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name);
        },
      toggleCallout:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name);
        },
      unsetCallout:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        view(editorView) {
          return new CalloutMenuView({ editor, view: editorView });
        },
      }),
    ];
  },
});

export default Callout;
